/**
 * Tendina con ricerca per scegliere gli attributi del grafico del Trend
 * (V62), sul modello di quella dei giocatori (`SceltaGiocatori`): le famiglie
 * in cima, che scelgono tutti i loro attributi in un tocco, poi gli attributi
 * divisi per famiglia, con l'icona, da spuntare uno a uno, ciascuno con il
 * pallino del colore della sua linea.
 */

import { Fragment, useState } from 'react';
import { ETICHETTA_FAMIGLIA_ATTRIBUTO, FAMIGLIE_ATTRIBUTO } from '@calcio/shared';
import { ChevronsUpDownIcon, GaugeIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { senzaTastieraSuTouch } from '../comuni/focus.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import type { AttributoTrend } from './trend.js';

/** Cerca per pezzo di nome, non fuzzy: il valore è «nome|id». */
function filtroNome(valore: string, cerca: string): number {
  const nome = valore.split('|')[0] ?? '';
  return nome.toLowerCase().includes(cerca.trim().toLowerCase()) ? 1 : 0;
}

export function SceltaAttributi({
  id,
  attributi,
  scelti,
  colore,
  onCambia,
}: {
  id: string;
  attributi: readonly AttributoTrend[];
  scelti: readonly string[];
  /** Il colore della linea dell'attributo; senza, il pallino resta vuoto. */
  colore: (id: string) => string | undefined;
  onCambia: (ids: string[]) => void;
}) {
  const [aperto, setAperto] = useState(false);
  const famiglie = FAMIGLIE_ATTRIBUTO.flatMap((f) => {
    const della = attributi.filter((a) => a.famiglia === f);
    return della.length ? [{ famiglia: f, attributi: della }] : [];
  });
  const set = new Set(scelti);
  const famigliaScelta = famiglie.find(
    (g) => g.attributi.length === set.size && g.attributi.every((a) => set.has(a.id)),
  )?.famiglia;
  const etichetta = famigliaScelta
    ? ETICHETTA_FAMIGLIA_ATTRIBUTO[famigliaScelta]
    : scelti.length === 0
      ? 'Nessuno'
      : scelti.length === 1
        ? (attributi.find((a) => a.id === scelti[0])?.nome ?? '1 attributo')
        : `${scelti.length} attributi`;
  const scambia = (aid: string) =>
    onCambia(scelti.includes(aid) ? scelti.filter((x) => x !== aid) : [...scelti, aid]);

  return (
    <Popover open={aperto} onOpenChange={setAperto}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aperto}
          className="justify-between font-normal"
        >
          <GaugeIcon data-icon="inline-start" />
          {etichetta}
          <ChevronsUpDownIcon data-icon="inline-end" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" onOpenAutoFocus={senzaTastieraSuTouch}>
        <Command filter={filtroNome}>
          <CommandInput placeholder="Cerca attributo…" />
          <CommandList>
            <CommandEmpty>Nessun attributo trovato.</CommandEmpty>
            <CommandGroup heading="Famiglie">
              {famiglie.map((g) => (
                <CommandItem
                  key={g.famiglia}
                  value={ETICHETTA_FAMIGLIA_ATTRIBUTO[g.famiglia]}
                  data-checked={famigliaScelta === g.famiglia}
                  onSelect={() => onCambia(g.attributi.map((a) => a.id))}
                >
                  <IconaFamiglia famiglia={g.famiglia} aria-hidden />
                  {ETICHETTA_FAMIGLIA_ATTRIBUTO[g.famiglia]}
                </CommandItem>
              ))}
              <CommandItem
                value="Nessuno"
                data-checked={scelti.length === 0}
                onSelect={() => onCambia([])}
              >
                Nessuno
              </CommandItem>
            </CommandGroup>
            {famiglie.map((g) => (
              <Fragment key={g.famiglia}>
                <CommandSeparator />
                <CommandGroup heading={ETICHETTA_FAMIGLIA_ATTRIBUTO[g.famiglia]}>
                  {g.attributi.map((a) => {
                    const c = colore(a.id);
                    return (
                      <CommandItem
                        key={a.id}
                        value={`${a.nome}|${a.id}`}
                        data-checked={scelti.includes(a.id)}
                        onSelect={() => scambia(a.id)}
                      >
                        <span
                          aria-hidden
                          className="size-2.5 shrink-0 rounded-full border border-border"
                          style={c ? { background: c, borderColor: c } : undefined}
                        />
                        {a.nome}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </Fragment>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
